import { Box, Circle, Flex, Icon, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import {
  hasUnreadChatsQueryOptions,
  unreadChatsCountQueryOptions,
} from '#features/chats/model/chats';
import { locales } from '#features/i18n';
import { Link } from '#features/routing';
import { AddIcon } from '#features/ui';
import { useHeightVar } from '#hooks/useHeightVar';
import { useNavigationItems } from '../useNavigationItems';

const localePattern = new RegExp(`^/(${Object.keys(locales).join('|')})(?=/|$)`);
const stripLocale = (value: string) => value.replace(localePattern, '');
const stripLocaleToken = (value: string) => value.replace('/{-$locale}', '');

export const BottomNavigation = () => {
  const { t } = useLingui();
  const { pathname } = useLocation();
  const isRouterLoading = useRouterState({ select: (s) => s.isLoading });
  const isAuth = useIsAuth();
  // Гость отсеивается ниже по коду, но хуки выполняются раньше — без `enabled` обе выборки
  // успевают уйти на каждой публичной странице и вернуться ошибкой «the user is not logged in».
  const { data: hasUnreadChats = false } = useQuery({
    ...hasUnreadChatsQueryOptions(),
    enabled: isAuth,
  });
  const { data: unreadChatsCount = 0 } = useQuery({
    ...unreadChatsCountQueryOptions(),
    enabled: isAuth,
  });
  const items = useNavigationItems();
  // Высота навигации уезжает в CSS-переменную: по ней окно разговора встаёт над панелью, а не
  // поверх неё, и на страницах без нижнего меню опускается к самому краю экрана.
  const [navNode, setNavNode] = useState<HTMLDivElement | null>(null);
  useHeightVar('--bottom-nav-height', navNode);
  const pathnameWithoutLocale = stripLocale(pathname);
  const isCreateRoute = pathnameWithoutLocale.startsWith('/dashboard/discover/create');
  if (!isAuth) {
    return null;
  }

  const visibleItems = items.filter(({ isProtected }) => {
    if (isProtected === true) {
      return true;
    }
    if (isProtected === false) {
      return false;
    }
    return true;
  });

  const mobileItems = visibleItems.filter(
    ({ to }) => !(typeof to === 'string' && to.includes('/feedback'))
  );

  const createItem = {
    name: t`dashboard:posts.createShort`,
    to: '/{-$locale}/dashboard/discover/create',
    icon: AddIcon,
    exact: true,
  };

  const mobileItemsWithCreate = (() => {
    const nextItems: typeof mobileItems = [];
    let inserted = false;

    for (const item of mobileItems) {
      nextItems.push(item);

      if (
        !inserted &&
        typeof item.to === 'string' &&
        item.to === '/{-$locale}/dashboard/discover'
      ) {
        nextItems.push(createItem);
        inserted = true;
      }
    }

    if (!inserted) {
      nextItems.push(createItem);
    }

    return nextItems;
  })();

  if (mobileItemsWithCreate.length === 0) {
    return null;
  }

  return (
    <Box
      // `asChild` вместо `as="nav"`: настоящий <nav> нужен, чтобы взять на себя ref, — Chakra с
      // `as` ref не типизирует вовсе. А ref здесь именно функция: состояние с узлом перезапускает
      // измерение и когда панель появляется, и когда исчезает.
      asChild
      bg="hsla(228, 11.6%, 8%, 0.78)"
      backdropFilter="blur(14px) saturate(130%)"
      WebkitBackdropFilter="blur(14px) saturate(130%)"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor="border"
      boxShadow="0 12px 30px rgba(0, 0, 0, 0.35)"
      display={{ base: 'block', md: 'none' }}
      left={{ base: 3, sm: 4 }}
      right={{ base: 3, sm: 4 }}
      position="fixed"
      bottom={{
        base: 'calc(env(safe-area-inset-bottom) + 16px)',
        sm: 'calc(env(safe-area-inset-bottom) + 20px)',
      }}
      w={{ base: 'auto', lg: '100%' }}
      zIndex="sticky"
      py={1}
    >
      <nav ref={setNavNode}>
        <Flex
          alignItems="flex-end"
          gap={1}
          justifyContent="space-between"
          maxW="1800px"
          mx="auto"
          px={3}
        >
          {mobileItemsWithCreate.map(({ to, name, icon, exact = true }) => {
            const isDiscoverItem =
              typeof to === 'string' && to === '/{-$locale}/dashboard/discover';
            const isCreateItem =
              typeof to === 'string' && to === '/{-$locale}/dashboard/discover/create';
            const isChatsItem = typeof to === 'string' && to === '/{-$locale}/dashboard/chats';
            const resolvedTo = typeof to === 'string' ? stripLocaleToken(to) : '';
            const isActive = isCreateItem
              ? isCreateRoute
              : isDiscoverItem
                ? pathnameWithoutLocale.startsWith('/dashboard/discover') && !isCreateRoute
                : resolvedTo.length > 0 &&
                  (exact
                    ? pathnameWithoutLocale === resolvedTo
                    : pathnameWithoutLocale.startsWith(resolvedTo));

            if (isCreateItem) {
              return (
                <Flex key={to} flex="1" justifyContent="center" alignSelf="center" minW="0" asChild>
                  <Link to={to} aria-label={name} aria-current={isActive ? 'page' : undefined}>
                    <Circle
                      size="46px"
                      bg={isActive ? 'purple.500' : 'purple.600'}
                      transition="background 0.2s ease"
                      _hover={{ bg: 'purple.500' }}
                    >
                      <Icon as={icon} boxSize="24px" color="white" />
                    </Circle>
                  </Link>
                </Flex>
              );
            }

            return (
              <Flex
                key={to}
                alignItems="center"
                borderRadius="md"
                color="fg.secondary"
                display="flex"
                flex="1"
                justifyContent="center"
                minW="0"
                px={1}
                py={1}
                textAlign="center"
                transition="color 0.2s ease"
                _hover={{ color: 'fg' }}
                asChild
              >
                <Link to={to} aria-label={name} aria-current={isActive ? 'page' : undefined}>
                  <Flex
                    alignItems="center"
                    color="inherit"
                    flexDirection="column"
                    gap={1}
                    justifyContent="center"
                    minH="36px"
                    px={2}
                    py={0}
                    transition="color 0.2s ease"
                  >
                    <Box
                      position="relative"
                      boxSize="22px"
                      animation={
                        isActive && isRouterLoading ? 'pulse 1s ease-in-out infinite' : undefined
                      }
                    >
                      <Icon as={icon} boxSize="22px" color={isActive ? 'white' : 'fg.secondary'} />
                      {isChatsItem && hasUnreadChats ? (
                        <Circle
                          position="absolute"
                          top="-4px"
                          right="-8px"
                          size="16px"
                          bg="purple.400"
                          color="white"
                          textStyle="micro"
                          borderWidth="1px"
                          borderColor="bg"
                          lineHeight="1"
                        >
                          {Math.min(unreadChatsCount, 99)}
                        </Circle>
                      ) : null}
                    </Box>
                    <Text
                      textStyle={isActive ? 'badge' : 'caption'}
                      lineHeight="14px"
                      maxW="90px"
                      minH="14px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      color={isActive ? 'white' : 'fg.muted'}
                    >
                      {name}
                    </Text>
                  </Flex>
                </Link>
              </Flex>
            );
          })}
        </Flex>
      </nav>
    </Box>
  );
};
